'use client';

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { usePathname } from 'next/navigation';
import { LocaleProvider } from '@/lib/i18n/client-provider';
import { type Locale } from '@/lib/i18n/config';
import { shouldRenderSupabaseSessionListenerForPath } from '@/lib/auth/public-auth';
import SupabaseSessionListener from '@/components/supabase-session-listener';
import { ConsentBanner } from '@/components/gdpr/ConsentBanner';

// Create query client with stable configuration
const createQueryClient = () => new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 1,
      retryDelay: 1000,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Create client once
let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always create a new query client
    return createQueryClient();
  } else {
    // Browser: create client once and reuse
    if (!browserQueryClient) browserQueryClient = createQueryClient();
    return browserQueryClient;
  }
}

interface AppProvidersProps {
  children: React.ReactNode;
  initialLocale: Locale;
  initialMessages: Record<string, unknown>;
  tenantId?: string;
}

export function AppProviders({ children, initialLocale, initialMessages, tenantId }: AppProvidersProps) {
  const [queryClient] = React.useState(() => getQueryClient());
  const disableSupabaseListener = process.env.NEXT_PUBLIC_DISABLE_SUPABASE_SESSION === 'true';
  const pathname = usePathname();
  const [hasMounted, setHasMounted] = React.useState(false);

  React.useEffect(() => {
    setHasMounted(true);
  }, []);

  // Delay rendering to keep server/client markup stable and avoid browser-only locks pre-hydration.
  const shouldRenderListener =
    hasMounted &&
    !disableSupabaseListener &&
    shouldRenderSupabaseSessionListenerForPath(pathname);
  
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem
        disableTransitionOnChange
      >
        <LocaleProvider
          key={tenantId ?? 'global-locale'}
          tenantId={tenantId}
          initialLocale={initialLocale}
          initialMessages={initialMessages}
        >
          {shouldRenderListener ? <SupabaseSessionListener /> : null}
          {children}
          <ConsentBanner />
        </LocaleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
